import {
    createAsyncThunk,
    createSelector,
    createSlice,
} from '@reduxjs/toolkit';
import { TRootState } from '../../../store';
import { sortAlphabetically } from '../../../helpers/comparators';
import {
    IGetProductEmptyAssignmentsParams,
    IRestPatchProduct,
    restGetProductAssignments,
    restGetProducts,
    restGetProductsEmptyAssignments,
    restPatchProductAssignments,
} from '../services';
import { ISourceSet, TSourceSetEntity, TStatus } from '../../../state/types';
import { processChunk } from '../../../components/TransferList/helpers/processChunk';
import { IProduct, IProductAssignment } from '../types';

interface IState {
    selectedProductId: null | number;
    mode: null | 'preview';
    emptyAssignments: {
        data: IProductAssignment[];
        status: TStatus;
    };
    assignments: {
        data: IProductAssignment[];
        status: TStatus;
    };
    products: {
        data: IProduct[] | null;
        status: TStatus;
    };
}

const MAX_ITEM_LIMIT = 1000;

const initialState: IState = {
    selectedProductId: null,
    mode: null,
    emptyAssignments: { data: [], status: 'idle' },
    assignments: { data: [], status: 'idle' },
    products: { data: null, status: 'idle' },
};

export const fetchProducts = createAsyncThunk('get:ui/products', async () => {
    return await restGetProducts();
});

export const fetchProductAssignments = createAsyncThunk(
    'get:ui/products/assignments',
    async (params: { id: number }) => {
        return await restGetProductAssignments(params.id);
    }
);
export const fetchProductEmptyAssignments = createAsyncThunk(
    'get:ui/products/emptyAssignments',
    async (params: { queryParams: IGetProductEmptyAssignmentsParams }) => {
        return await restGetProductsEmptyAssignments(params.queryParams);
    }
);

const mapMonitoredToRelationIds = (
    collection: IProductAssignment[]
): IRestPatchProduct[] => {
    return collection.map((p) => ({ monitoredId: p.relationId }));
};
export const patchRegistersProductAssignments = async (
    id: number,
    added: IRestPatchProduct[],
    removed: IRestPatchProduct[]
) => {
    const assignmentsDataChunk = Math.floor(
        (added.length + removed.length) / MAX_ITEM_LIMIT
    );

    if (assignmentsDataChunk >= 1) {
        await processChunk(id, added, removed, restPatchProductAssignments);
        return Promise.resolve();
    }

    const response = await restPatchProductAssignments(id, {
        added: added,
        removed: removed,
    });
    return response;
};

export const patchProductAssignments = createAsyncThunk(
    'patch:ui/products/assignments',
    async (payload: {
        id: number;
        data: {
            added: IProductAssignment[];
            removed: IProductAssignment[];
        };
    }) => {
        const { id, data } = payload;

        const response = await patchRegistersProductAssignments(
            id,
            mapMonitoredToRelationIds(data.added),
            mapMonitoredToRelationIds(data.removed)
        );
        return response;
    }
);

const products = createSlice({
    name: 'productsSlice',
    initialState,
    reducers: {
        goToPreviewMode(state) {
            state.mode = 'preview';
        },
        resetMode(state) {
            state.mode = null;
        },
        clearEmptyAssignments(state) {
            state.emptyAssignments.status = 'done';
            state.emptyAssignments.data = [];
        },
        selectProduct(state, action) {
            state.selectedProductId = action.payload;
            state.mode = 'preview';
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchProducts.pending, (state) => {
                state.products.status = 'loading';
            })
            .addCase(fetchProducts.fulfilled, (state, action) => {
                state.products.data = action.payload;
                state.products.status = 'done';
            })
            .addCase(fetchProductAssignments.pending, (state) => {
                state.assignments.status = 'loading';
            })
            .addCase(fetchProductAssignments.fulfilled, (state, action) => {
                state.assignments.data = action.payload.sort((a, b) =>
                    sortAlphabetically(a.name, b.name)
                );
                state.assignments.status = 'done';
            })
            .addCase(fetchProductEmptyAssignments.pending, (state) => {
                state.emptyAssignments.status = 'loading';
            })
            .addCase(
                fetchProductEmptyAssignments.fulfilled,
                (state, action) => {
                    if (
                        action.meta.arg.queryParams.excludedProductId ===
                        state.selectedProductId
                    ) {
                        state.emptyAssignments.data = action.payload;
                        state.emptyAssignments.status = 'done';
                    }
                }
            );
    },
});
const getCurrent = (state: TRootState) => state.ui.products;

export const getProductsMode = (state: TRootState) => getCurrent(state).mode;

export const getSelectedProductId = (state: TRootState) =>
    getCurrent(state).selectedProductId;

export const getAssignments = (state: TRootState) =>
    getCurrent(state).assignments.data;
export const getAssignmentsStatus = (state: TRootState) =>
    getCurrent(state).assignments.status;

export const getEmptyAssignments = (state: TRootState) =>
    getCurrent(state).emptyAssignments.data;
export const getEmptyAssignmentsStatus = (state: TRootState) =>
    getCurrent(state).emptyAssignments.status;

export const isProductsPaneVisible = createSelector(
    [getProductsMode],
    (mode) => !!mode
);

export const getProductsCollection = (state: TRootState) =>
    getCurrent(state).products.data;

export const getSelectedProduct = createSelector(
    [getSelectedProductId, getProductsCollection],
    (id, collection) =>
        (id && collection && collection.find((p: IProduct) => p.id === id)) ||
        null
);

export const getProductsAsSourceSet = createSelector(
    [getProductsCollection],
    (products): ISourceSet | null =>
        products && {
            id: 'products',
            definitionId: 'products',
            label: 'Products',
            attributes: [
                { id: 'name', label: 'Name', type: 'text' },
                { id: 'description', label: 'Description', type: 'text' },
                {
                    id: 'tags',
                    label: 'Tags',
                    type: 'list',
                    formatting: {
                        separator: ', ',
                    },
                },
            ],
            layersAttributes: [],
            _meta: {},
            entities: products.map(
                (product: IProduct): TSourceSetEntity => ({
                    ...product,
                    id: product.id.toString(),
                    _meta: {},
                })
            ),
        }
);

export const { selectProduct, clearEmptyAssignments, resetMode } =
    products.actions;

export default products.reducer;
